import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from 'react-router-dom';

interface HeaderTabProps {
    tabName: string;
    tabLink: string;
    subMenu?: Array<Array<string>>;
}

const HeaderTab: React.FC<HeaderTabProps> = ({ tabName, tabLink, subMenu }) => {
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1082);
    const dropdownRef = useRef<HTMLLIElement>(null);
    const navigate = useNavigate();

    // Function to clear URL and navigate to the target page
    const handleClearUrlAndNavigate = () => {
        navigate("", { replace: true });  // Replace the current entry in the history stack
    };
    
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    
    const submenuOnClick = () => {
        handleClearUrlAndNavigate();
        scrollToTop();
        setDropdownVisible(false);
    };

    // Detect mobile screen size
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 1082);
        };
        
        window.addEventListener('resize', checkIfMobile);
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Toggle dropdown on click (for touch devices)
    const toggleDropdown = (e: React.MouseEvent) => {
        if (subMenu && subMenu.length > 0 && isMobile) {
            e.preventDefault();
            setDropdownVisible(!isDropdownVisible);
        }
    };

    const tabStyle = {
        padding: "28px 15px 20px",
        margin: "0px 3px",
        display: "inline-block",
        position: "relative" as "relative",
    };

    const innerTabStyle = {
        fontFamily: "'Play', Arial",
        textDecoration: "none",
        color: "white",
        width: "auto",
        scrollBehavior: "smooth" as const,
    };

    const dropdownStyle = {
        position: "absolute" as "absolute",
        top: "100%", // Position it right below the tab
        left: 0,
        backgroundColor: "#333",
        display: "none",
        minWidth: "160px",
        zIndex: 1,
        listStyleType: "none" as const,
    };

    const showDropdownStyle = {
        ...dropdownStyle,
        display: "block"
    };


    return (
        <li
            ref={dropdownRef}
            style={tabStyle}
            className="header-tab"
            onMouseEnter={() => setDropdownVisible(true)} // Show dropdown on hover
            onMouseLeave={() => setDropdownVisible(false)} // Hide dropdown when not hovering
            onClick={toggleDropdown} // Toggle dropdown on click for touch devices
        >
            
            {tabLink.startsWith('/') ? (
                <Link to={tabLink} style={innerTabStyle} onClick={(e) => {
                    if (isMobile && subMenu && subMenu.length > 0) {
                        e.preventDefault(); // On mobile, prevent navigation if has submenu
                    } else {
                        handleClearUrlAndNavigate();
                        setDropdownVisible(false);
                    }
                }}>{tabName}</Link>
            ) : (
                <a href={tabLink || '#'} style={innerTabStyle} onClick={(e) => {
                    if (!tabLink || (isMobile && subMenu && subMenu.length > 0)) {
                        e.preventDefault(); // Prevent navigation if no link or mobile with submenu
                    } else if (tabLink.startsWith('#')) {
                        // For hash links, scroll to element on current page
                        e.preventDefault();
                        const id = tabLink.substring(1);
                        const element = document.getElementById(id);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                        setDropdownVisible(false);
                    } else {
                        handleClearUrlAndNavigate();
                        setDropdownVisible(false);
                    }
                }}>{tabName}</a>
            )}

            {/* Render submenu only if it exists and has at least one item */}
            {subMenu && subMenu.length > 0 && (
                <ul className="dropdown-menu" style={isDropdownVisible ? showDropdownStyle : dropdownStyle}>
                    {subMenu.map(([subTabName, subTabLink], index) => (
                        <li key={index}>
                            {subTabLink.startsWith('/') ? (
                                <Link to={subTabLink} style={innerTabStyle} onClick={() => submenuOnClick()} >{subTabName}</Link>
                            ) : (
                                <a href={subTabLink} style={innerTabStyle} onClick={(e) => {
                                    if (subTabLink.startsWith('#')) {
                                        // For hash links, scroll to element on current page
                                        e.preventDefault();
                                        const id = subTabLink.substring(1);
                                        const element = document.getElementById(id);
                                        if (element) {
                                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        }
                                        setDropdownVisible(false);
                                    } else {
                                        submenuOnClick();
                                    }
                                }} >{subTabName}</a>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export default HeaderTab;
